import type { SurveyAnswer, SurveyQuestion } from './types';

type SurveyQuestionInputProps = {
  question: SurveyQuestion;
  answer: SurveyAnswer;
  onChange: (answer: SurveyAnswer) => void;
};

export function SurveyQuestionInput({
  question,
  answer,
  onChange,
}: SurveyQuestionInputProps) {
  if (question.type === 'single') {
    return (
      <div className="mb-7 flex flex-col gap-2.5">
        {question.options.map((option, optionIndex) => (
          <button
            key={option}
            type="button"
            data-testid={`survey-option-single-${optionIndex}`}
            onClick={() => onChange(optionIndex)}
            className={`flex items-center gap-3.5 rounded-xl border-[1.5px] px-[18px] py-4 text-left text-[15px] transition-colors ${
              answer === optionIndex
                ? 'border-primary bg-primary-pale font-medium text-ink'
                : 'border-rule bg-surface text-ink-soft hover:border-ink-muted'
            }`}
          >
            <span
              className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 ${answer === optionIndex ? 'border-primary' : 'border-rule'}`}
            >
              {answer === optionIndex && (
                <span className="h-2.5 w-2.5 rounded-full bg-primary" />
              )}
            </span>
            <span>{option}</span>
          </button>
        ))}
      </div>
    );
  }

  if (question.type === 'multi') {
    const selected = Array.isArray(answer) ? answer : [];
    const lastIndex = question.options.length - 1;
    return (
      <div className="mb-7 flex flex-col gap-2">
        {question.options.map((option, optionIndex) => {
          const isSelected = selected.includes(optionIndex);
          return (
            <button
              key={option}
              type="button"
              data-testid={`survey-option-multi-${optionIndex}`}
              onClick={() => {
                if (question.exclusiveLast && optionIndex === lastIndex) {
                  onChange(isSelected ? [] : [optionIndex]);
                  return;
                }
                const next = isSelected
                  ? selected.filter((value) => value !== optionIndex)
                  : [
                      ...selected.filter(
                        (value) =>
                          !question.exclusiveLast || value !== lastIndex
                      ),
                      optionIndex,
                    ];
                onChange(next);
              }}
              className={`flex items-center gap-3.5 rounded-xl border-[1.5px] px-4 py-3.5 text-left text-sm transition-colors ${
                isSelected
                  ? 'border-primary bg-primary-pale font-medium text-ink'
                  : 'border-rule bg-surface text-ink-soft hover:border-ink-muted'
              }`}
            >
              <span
                className={`grid h-5 w-5 shrink-0 place-items-center rounded-md border-2 ${isSelected ? 'border-primary bg-primary' : 'border-rule'}`}
              >
                {isSelected && (
                  <span className="h-2 w-1 rotate-45 border-b-2 border-r-2 border-white" />
                )}
              </span>
              <span>{option}</span>
            </button>
          );
        })}
      </div>
    );
  }

  if (question.type === 'multi-grouped') {
    const selected = Array.isArray(answer) ? answer : [];
    let globalIndex = 0;
    return (
      <div className="mb-7 space-y-6">
        {question.groups.map((group) => (
          <div key={group.label}>
            <h4 className="mb-3 font-mono text-[11px] font-medium uppercase tracking-[0.1em] text-ink-faint">
              {group.label}
            </h4>
            <div className="flex flex-wrap gap-2">
              {group.options.map((option) => {
                const optionIndex = globalIndex;
                globalIndex += 1;
                const isSelected = selected.includes(optionIndex);
                return (
                  <button
                    key={option}
                    type="button"
                    data-testid={`survey-option-grouped-${optionIndex}`}
                    onClick={() =>
                      onChange(
                        isSelected
                          ? selected.filter((value) => value !== optionIndex)
                          : [...selected, optionIndex]
                      )
                    }
                    className={`rounded-full border-[1.5px] px-3.5 py-2 text-[13px] transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary text-white'
                        : 'border-rule bg-surface text-ink-soft hover:border-ink-muted'
                    }`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (question.type === 'rating') {
    const ratingAnswer =
      answer && typeof answer === 'object' && !Array.isArray(answer)
        ? answer
        : {};
    return (
      <div className="mb-7 flex flex-col">
        {question.rows.map((row, rowIndex) => (
          <div
            key={row}
            className="border-b border-rule-light py-4 first:pt-1 last:border-b-0 last:pb-1"
          >
            <span className="mb-2.5 block text-[15px] font-medium text-ink">
              {row}
            </span>
            <div className="grid grid-cols-3 gap-2">
              {question.scale.map((label, scaleIndex) => (
                <button
                  key={label}
                  type="button"
                  data-testid={`survey-rating-${rowIndex}-${scaleIndex}`}
                  onClick={() =>
                    onChange({ ...ratingAnswer, [rowIndex]: scaleIndex })
                  }
                  className={`rounded-[10px] border-[1.5px] px-1.5 py-2.5 text-center text-[13px] leading-tight transition-colors ${
                    ratingAnswer[rowIndex] === scaleIndex
                      ? 'border-primary bg-primary-pale font-semibold text-primary'
                      : 'border-rule bg-surface text-ink-muted hover:border-ink-muted hover:text-ink-soft'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <textarea
      data-testid="survey-text-input"
      value={typeof answer === 'string' ? answer : ''}
      onChange={(event) => onChange(event.target.value)}
      placeholder={question.placeholder}
      className="mb-7 min-h-[120px] w-full resize-y rounded-xl border-[1.5px] border-rule bg-surface px-4 py-3.5 text-[15px] leading-[1.55] text-ink outline-none transition-colors focus:border-primary focus:shadow-[0_0_0_3px_rgba(43,92,255,0.12)]"
    />
  );
}
